import { Grid, GridSize } from '@material-ui/core';
import { useControlledExpandBehavior } from 'design-system';
import { uniqueId } from 'lodash';
import React, { useMemo } from 'react';
import { appliesNewAutoPxDiscount } from 'src/store/quotation/actions/helperMethods';

// Data layer
import { mustShowAllUnits } from 'data-layer-cr/dist/functions/quotations';
import {
  CoverageCatalog,
  CoverageCatalogResume,
  Quotation,
  QuotationUnit,
} from 'data-layer-cr/dist/models';

// Components
import PricingProfileResponse from '../../../../../../models/responseTypes/PricingProfileResponse';
import DeductibleDetailsCard from '../DeductibleDetailsCard';
import DeductibleVariantCard from '../DeductibleVariantCard';

type Props = {
  quotation: Quotation;
  coverageCatalogs: CoverageCatalog[];
  coverageCatalogResumes: CoverageCatalogResume[];
  deductibleGridSize: GridSize;
  currentProfile: PricingProfileResponse;
};

/**
 * This container object was defined so that each quotation unit instance could be associated with a
 * unique identifier that persist between renders. This is required because units can be displayed inside
 * a list, and the "key" element is what will decide what elements need to rerender (uniqueIdentifier
 * persisting between renders will avoid each list element from being re-created).
 *
 * Ideally, the server should return an "id" reference for this object, but since it is an intermediate
 * entity there is no direct way to identify a single instance other than applying the wrapper.
 */
type UnitDisplayContainer = {
  uniqueIdentifier: string;
  unit: QuotationUnit;
};

const DeductibleSectionWrapper = ({
  quotation,
  coverageCatalogs,
  coverageCatalogResumes,
  deductibleGridSize,
  currentProfile,
}: Props): JSX.Element => {
  const showMultiple = useMemo(() => mustShowAllUnits(quotation), [quotation]);
  const { isExpanded, toggleExpandStatus } = useControlledExpandBehavior({
    defaultExpanded: '',
  });
  const maxPayments = appliesNewAutoPxDiscount(quotation.isAutoPx) ? 11 : 12;

  const mappedUnits = useMemo(
    () =>
      quotation.units
        .map(
          (unit): UnitDisplayContainer => ({
            uniqueIdentifier: uniqueId(),
            unit,
          }),
        )
        .sort((a, b) => b.unit.total.grandTotal - a.unit.total.grandTotal),
    [quotation.units],
  );

  const variantList = useMemo(
    () =>
      mappedUnits.map((mappedUnit): React.ReactNode => {
        const { uniqueIdentifier, unit } = mappedUnit;
        const unitPackageFilter = {
          ...unit,
          package: {
            ...unit.package,
            description: `Precio`,
          },
        };

        return (
          <Grid item xs={12} md={deductibleGridSize} key={uniqueIdentifier}>
            <DeductibleVariantCard
              data-testid={uniqueIdentifier}
              isAutoPx={quotation.isAutoPx}
              maxPayments={maxPayments}
              unit={unitPackageFilter}
              coverageCatalogs={coverageCatalogs}
              collapsed={!isExpanded(uniqueIdentifier)}
              onCollapsedUpdate={(): void =>
                toggleExpandStatus(uniqueIdentifier)
              }
              currentProfile={currentProfile}
            />
          </Grid>
        );
      }),
    [
      isExpanded,
      toggleExpandStatus,
      mappedUnits,
      coverageCatalogs,
      quotation,
      maxPayments,
      deductibleGridSize,
      currentProfile,
    ],
  );

  if (!showMultiple) {
    return (
      <Grid container spacing={2} data-testid="deductible-variant-list">
        {variantList}
      </Grid>
    );
  }
  return (
    <DeductibleDetailsCard
      data-testid="deductible-details-card"
      quotation={quotation}
      coverages={coverageCatalogs}
      coverageResumes={coverageCatalogResumes}
    />
  );
};

export default DeductibleSectionWrapper;
